// client-side imagekit helpers: auth, direct uploads, and image grouping utilities.
// browser uploads should use uploadClientFile / uploadClientImage from ./client-upload.
// does not perform server-side rename or prisma persistence — see ./server.ts.
// depends on: ./client-upload, @/lib/network, @prisma/client types.
//
// uploadMultipleImages — batch picker flows (embed form, jobs photos, project modal).
//   Fetches N auth tokens in one request, uploads in parallel. Caller saves each result.
// uploadSingleImage — internal/batch use only; pass a pre-fetched auth token per file.

import { MAX_FILE_SIZE_MB } from "@/lib/constants/photos";
import { requestWithRetry } from "@/lib/network";
import { sanitizeIfSVG } from "@/lib/utils/svgSanitizer";

import { deriveDefaultFileName, getImagekitAuth, imagekit } from "./client-upload";

import type { ImagekitAuth } from "@/types/imagekit";
import type { UploadResponse } from "imagekit-javascript/dist/src/interfaces";
import type { Image, Prisma } from "@prisma/client";

export type ImageWithTaggedServices = Prisma.ImageGetPayload<{
  include: {
    taggedServices: true;
  };
}>;

type ImagekitUploadFile = File | Blob;

type SingleUploadImageProps = {
  imageFile: ImagekitUploadFile;
  auth: Pick<ImagekitAuth, "signature" | "expire" | "token">;
};

/** Low-level single upload when the caller already holds an ImageKit auth token. Prefer uploadClientImage or uploadMultipleImages. */
async function uploadSingleImage({ imageFile, auth }: SingleUploadImageProps): Promise<UploadResponse | null> {
  try {
    const processedFile = await sanitizeIfSVG(imageFile);
    const fileName = deriveDefaultFileName(processedFile);

    return await requestWithRetry(() =>
      imagekit.upload({
        file: processedFile,
        fileName,
        useUniqueFileName: true,
        signature: auth.signature,
        expire: auth.expire,
        token: auth.token,
        checks: `"file.size" <= "${MAX_FILE_SIZE_MB}MB"`,
      })
    );
  } catch {
    return null;
  }
}

/**
 * Upload several files in one user action. Fetches one auth token per file, then uploads in parallel.
 * Use for multi-select pickers (embed form, jobs photos, project images). Caller handles each result.
 */
async function uploadMultipleImages(images: ImagekitUploadFile[]) {
  if (images.length === 0) {
    return [];
  }

  const auths = await getImagekitAuth(images.length);

  if (auths.length !== images.length) {
    throw new Error("Error uploading images, please try again shortly or contact support.");
  }

  const uploadPromises = images.map((image, i) => uploadSingleImage({ imageFile: image, auth: auths[i] }));

  return Promise.all(uploadPromises);
}

function separateImagesByTaggedService(images: ImageWithTaggedServices[]) {
  const result: Record<string, ImageWithTaggedServices[] | Image[]> = { untagged: [], allImages: [] };

  if (!images || !images.length) return result;

  images.forEach((image) => {
    result.allImages.push(image);
    const { taggedServices = [] } = image;

    if (!taggedServices.length) {
      result.untagged.push(image);
      return;
    }

    taggedServices.forEach((service) => {
      if (!result[service.name]) {
        result[service.name] = [];
      }
      result[service.name].push(image);
    });
  });

  for (const key of Object.keys(result)) {
    result[key] = result[key].sort(sortByCreatedAt);
  }

  return result;
}

function sortByCreatedAt(a: { createdAt: Date }, b: { createdAt: Date }) {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

export {
  CONTENT_IMAGE_TRANSFORMATION,
  getImagekitAuth,
  getTransformedImageKitUrl,
  imagekit,
  uploadClientFile,
  uploadClientImage,
  uploadEditorContentImage,
} from "./client-upload";

export { separateImagesByTaggedService, sortByCreatedAt, uploadMultipleImages, uploadSingleImage };
